import {createTheme, alpha} from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Design tokens — single source of truth
// ---------------------------------------------------------------------------
const NAVY = '#1B3A6B';
const TEAL = '#1D6B5E';
const BORDER = '#DDE1E8'; // unified — was split between #e0e0e0 and #DDE1E8

const theme = createTheme({
    // -------------------------------------------------------------------------
    // Palette
    // -------------------------------------------------------------------------
    palette: {
        primary: {
            main: NAVY,
            light: '#2F5FA8',
            dark: '#0F2347',
            contrastText: '#fff',
        },
        secondary: {
            main: TEAL,
            light: '#2A9986',
            dark: '#124840',
            contrastText: '#fff',
        },
        error: {
            main: '#9B2226',
            light: '#C0393E',
            dark: '#6E181B',
            contrastText: '#fff',
        },
        warning: {
            main: '#B45309',
            light: '#D97706',
            dark: '#8A3E04',
            contrastText: '#fff',
        },
        success: {
            main: TEAL,
            light: '#2A9986',
            dark: '#124840',
            contrastText: '#fff',
        },
        info: {
            main: NAVY,
            light: '#2F5FA8',
            dark: '#0F2347',
            contrastText: '#fff',
        },
        background: {
            default: '#F4F5F7',
            paper: '#ffffff',
        },
        divider: BORDER,
        text: {
            primary: '#111827',
            secondary: '#4B5563',
            disabled: '#9CA3AF',
        },
        action: {
            hover: alpha(NAVY, 0.06),
            selected: alpha(NAVY, 0.10),
            selectedOpacity: 0.10,
            hoverOpacity: 0.06,
            focus: alpha(NAVY, 0.12),
            focusOpacity: 0.12,
            activatedOpacity: 0.16,
        },
        // Custom status palette — augmented above
        status: {
            active: NAVY,
            pending: '#B45309',
            overdue: '#9B2226',
            legal: '#4A2C8A',
            settled: TEAL,
            written_off: '#4A4A47',
        },
    },

    // -------------------------------------------------------------------------
    // Typography — compact sizes retained by design; system font stack
    // -------------------------------------------------------------------------
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
        ].join(','),

        fontSize: 13,

        h1: {
            fontSize: '1.75rem',
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.25rem',
            fontWeight: 500,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.125rem',
            fontWeight: 500,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        h6: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
            textTransform: 'none',
            letterSpacing: '0.05em',
        },
        body1: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.8125rem',
            lineHeight: 1.43,
        },
        button: {
            fontSize: '0.8125rem',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.02em',
        },
        caption: {
            fontSize: '0.75rem',
            lineHeight: 1.4,
            // color removed — was hardcoded #616161, use text.secondary token at call site
        },
        overline: {
            fontSize: '0.6875rem',
            fontWeight: 600,
            lineHeight: 2,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
        },
    },

    // -------------------------------------------------------------------------
    // Shape
    // -------------------------------------------------------------------------
    shape: {
        borderRadius: 4,
    },

    // -------------------------------------------------------------------------
    // Spacing
    // -------------------------------------------------------------------------
    spacing: 8,

    // -------------------------------------------------------------------------
    // Shadows — full 25-value array, systematically scaled
    // -------------------------------------------------------------------------
    shadows: [
        'none',                                                    // 0
        '0px 1px 2px rgba(0,0,0,0.05)',                            // 1  subtle lift
        '0px 1px 4px rgba(0,0,0,0.08)',                            // 2  cards
        '0px 2px 6px rgba(0,0,0,0.10)',                            // 3  dropdowns
        '0px 4px 8px rgba(0,0,0,0.12)',                            // 4  modals
        '0px 6px 12px rgba(0,0,0,0.14)',                           // 5  popovers
        '0px 6px 14px rgba(0,0,0,0.14)',                           // 6
        '0px 7px 16px rgba(0,0,0,0.14)',                           // 7
        '0px 8px 18px rgba(0,0,0,0.14)',                           // 8
        '0px 9px 20px rgba(0,0,0,0.14)',                           // 9
        '0px 10px 22px rgba(0,0,0,0.14)',                          // 10
        '0px 11px 24px rgba(0,0,0,0.15)',                          // 11
        '0px 12px 26px rgba(0,0,0,0.15)',                          // 12
        '0px 13px 28px rgba(0,0,0,0.15)',                          // 13
        '0px 14px 30px rgba(0,0,0,0.15)',                          // 14
        '0px 15px 32px rgba(0,0,0,0.15)',                          // 15
        '0px 16px 34px rgba(0,0,0,0.15)',                          // 16
        '0px 17px 36px rgba(0,0,0,0.15)',                          // 17
        '0px 18px 38px rgba(0,0,0,0.15)',                          // 18
        '0px 20px 40px rgba(0,0,0,0.15)',                          // 19
        '0px 22px 44px rgba(0,0,0,0.15)',                          // 20
        '0px 24px 48px rgba(0,0,0,0.15)',                          // 21
        '0px 26px 52px rgba(0,0,0,0.15)',                          // 22
        '0px 28px 56px rgba(0,0,0,0.15)',                          // 23
        '0px 30px 60px rgba(0,0,0,0.15)',                          // 24
    ],

    // -------------------------------------------------------------------------
    // Component overrides
    // -------------------------------------------------------------------------
    components: {
        MuiCssBaseline: {
            styleOverrides: `
        @keyframes ds-spin    { to { transform: rotate(360deg); } }
        @keyframes ds-slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ds-fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ds-scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        ::-webkit-scrollbar       { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
      `,
        },

        // === BUTTONS ===
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    padding: '6px 16px',
                    minHeight: 32,
                    fontWeight: 500,
                    transition: 'all 0.15s ease-in-out',
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0px 2px 4px rgba(0,0,0,0.10)',
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': {
                        borderWidth: '1.5px',
                    },
                },
                sizeSmall: {
                    padding: '4px 12px',
                    minHeight: 28,
                    fontSize: '0.75rem',
                },
                sizeLarge: {
                    padding: '8px 20px',
                    minHeight: 40,
                    fontSize: '0.9375rem',
                },
            },
        },

        // === CARDS & PAPER ===
        MuiCard: {
            defaultProps: {
                elevation: 1,
            },
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    border: `1px solid ${BORDER}`,
                    boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                    borderBottom: `1px solid ${BORDER}`,
                    backgroundColor: '#F4F5F7', // matches background.default
                },
                title: {
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: '#111827',
                },
                subheader: {
                    fontSize: '0.75rem',
                    marginTop: '2px',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '16px',
                    '&:last-child': {
                        paddingBottom: '16px',
                    },
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 1,
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                rounded: {
                    borderRadius: 6,
                },
            },
        },

        // === TABLES ===
        MuiTable: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    border: `1px solid ${BORDER}`,
                    borderRadius: 6,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: '#F4F5F7',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${BORDER}`,
                    fontSize: '0.8125rem',
                    padding: '10px 12px',
                },
                head: {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#4B5563',
                    padding: '12px 12px',
                    backgroundColor: '#F4F5F7',
                    borderBottom: `2px solid ${BORDER}`,
                },
                sizeSmall: {
                    padding: '6px 10px',
                    '&.MuiTableCell-head': {
                        padding: '8px 10px',
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: alpha(NAVY, 0.04),
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(NAVY, 0.08),
                        '&:hover': {
                            backgroundColor: alpha(NAVY, 0.12),
                        },
                    },
                },
            },
        },

        // === TABS ===
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                    borderBottom: `2px solid ${BORDER}`,
                },
                indicator: {
                    height: 3,
                    backgroundColor: NAVY,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                    minWidth: 80,
                    padding: '8px 16px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#4B5563',
                    '&.Mui-selected': {
                        color: NAVY,
                        fontWeight: 600,
                    },
                    '&:hover': {
                        color: NAVY,
                        backgroundColor: alpha(NAVY, 0.06),
                    },
                },
            },
        },

        // === FORM FIELDS ===
        MuiTextField: {
            defaultProps: {
                size: 'small',
                variant: 'outlined',
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: NAVY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px',
                        borderColor: NAVY,
                    },
                },
                input: {
                    padding: '8px 12px',
                    fontSize: '0.875rem',
                },
                notchedOutline: {
                    borderColor: BORDER,
                },
            },
        },
        MuiInputLabel: {
            defaultProps: {
                shrink: true,
            },
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#4B5563',
                    '&.Mui-focused': {
                        color: NAVY,
                    },
                },
                outlined: {
                    transform: 'translate(14px, -9px) scale(0.9)',
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.9)',
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#4B5563',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '0.6875rem',
                    marginTop: '4px',
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    padding: '8px 12px',
                    minHeight: 'auto',
                },
            },
        },

        // === CHIPS ===
        MuiChip: {
            styleOverrides: {
                root: {
                    height: 24,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    borderRadius: 4,
                },
                label: {
                    padding: '0 8px',
                },
                sizeSmall: {
                    height: 20,
                    fontSize: '0.625rem',
                },
            },
        },

        // === ALERTS — updated to match legal palette ===
        MuiAlert: {
            styleOverrides: {
                root: {
                    fontSize: '0.8125rem',
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: '1px solid',
                },
                standardError: {
                    backgroundColor: '#FDECEA',
                    borderColor: '#C0393E',
                    color: '#6E181B',
                },
                standardWarning: {
                    backgroundColor: '#FEF3C7',
                    borderColor: '#D97706',
                    color: '#8A3E04',
                },
                standardInfo: {
                    backgroundColor: '#E8EEF8',
                    borderColor: '#2F5FA8',
                    color: '#0F2347',
                },
                standardSuccess: {
                    backgroundColor: '#E4F2EF',
                    borderColor: '#2A9986',
                    color: '#124840',
                },
                message: {
                    padding: '0',
                },
            },
        },

        // === DIALOGS ===
        MuiDialog: {
            styleOverrides: {
                paper: {
                    boxShadow: '0px 8px 24px rgba(0,0,0,0.15)',
                    borderRadius: 8,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    padding: '16px 20px',
                    borderBottom: `1px solid ${BORDER}`,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '20px',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 20px',
                    borderTop: `1px solid ${BORDER}`,
                },
            },
        },

        // === TOOLTIPS ===
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#1a1a2e',
                    fontSize: '0.75rem',
                    padding: '6px 10px',
                    borderRadius: 4,
                },
                arrow: {
                    color: '#1a1a2e',
                },
            },
        },

        // === DIVIDERS ===
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: BORDER,
                },
            },
        },
    },
});

export default theme;